import { Stack, Tooltip } from '@mui/material';
import MUIModal from '@mui/material/Modal';
import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { Agent } from '../../../interfaces/Agent';
import { ColorContext } from '../../../interfaces/App';
import { ChatAgent } from '../../../interfaces/ChatAgent';
import Knowledge from '../../../interfaces/Knowledge';
import Button from '../Button/Button';
import Text from '../Text/Text';
import StyledModal from './StyledModal';

type ModalProps = {
  confirmText?: string;
  confirmButtonColor?: ColorContext;
  description?: string;
  denyText?: string;
  item?: Knowledge | Agent | ChatAgent; // update this if deleting an item
  mainPromptText: string;
  modalShouldBeOpen?: boolean;
  openModalButtonColor?: ColorContext;
  openModalButtonText?: string;
  openModalButtonSize?: 'small' | 'medium' | 'large'; // MUI Button size options;
  openModalButtonVariant?: 'text' | 'outlined' | 'contained';
  openModalButtonFullWidth?: boolean;
  openModalIcon?: ReactNode;
  showButton?: boolean;
  toolTipText?: string;
  buttonDisabled?: boolean;
  callback?: ((id?: any) => Promise<void>) | (() => void);
  /** Runs when the user dismisses via the deny/outlined button, before the modal closes. */
  denyCallback?: () => void;
  setChangeStatusModalOpen?: (state: boolean) => void;
  children?: ReactNode;
};

const Modal: React.FC<ModalProps> = ({
  confirmText,
  confirmButtonColor = 'error',
  description,
  denyText,
  item,
  mainPromptText,
  modalShouldBeOpen = false,
  openModalButtonText,
  openModalButtonColor = 'inherit',
  openModalButtonVariant = 'outlined',
  openModalButtonSize = 'small',
  openModalButtonFullWidth = false,
  openModalIcon,
  showButton = true,
  toolTipText = '',
  buttonDisabled = false,
  callback,
  denyCallback,
  setChangeStatusModalOpen,
  children,
}) => {
  const [open, setOpen] = React.useState(modalShouldBeOpen);
  const { t } = useTranslation();
  const isIconOnlyTrigger = Boolean(openModalIcon && !openModalButtonText);
  const triggerLabel =
    openModalButtonText ?? (openModalIcon ? '' : t('Delete'));

  const handleOpen = () => {
    setChangeStatusModalOpen?.(true);
    setOpen(true);
  };
  const handleClose = () => {
    setChangeStatusModalOpen?.(false);
    setOpen(false);
  };

  useEffect(() => {
    setOpen(modalShouldBeOpen);
  }, [modalShouldBeOpen]);

  return (
    <>
      {showButton && (
        <Tooltip title={toolTipText} placement="right-start" arrow>
          <span>
            <Button
              color={openModalButtonColor}
              onClick={handleOpen}
              size={openModalButtonSize}
              variant={openModalButtonVariant}
              fullWidth={openModalButtonFullWidth}
              disabled={buttonDisabled}
              style={
                isIconOnlyTrigger
                  ? {
                      minWidth: '44px',
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      background: '#F1F5F9',
                      borderColor: 'rgba(15, 23, 42, 0.06)',
                      color: '#1841ff',
                      padding: '0.5rem',
                    }
                  : undefined
              }
            >
              {openModalIcon}
              {triggerLabel}
            </Button>
          </span>
        </Tooltip>
      )}
      <MUIModal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <StyledModal id="modal-box">
          <Text type="subtitle1" lineHeight="revert" letterSpacing="revert">
            {mainPromptText}
          </Text>
          <Text type="body1">
            {description ||
              t('This action is permanent and cannot be reversed.')}
          </Text>
          {children}
          <Stack
            direction="row"
            gap="1rem"
            justifyContent="flex-end"
            marginTop="1rem"
          >
            {callback && (
              <Button
                size="small"
                variant="contained"
                color={confirmButtonColor}
                padding="0.5rem 1.5rem"
                onClick={() => {
                  callback(item?.id as string);
                  handleClose();
                }}
              >
                {confirmText || t('Yes')}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              padding="0.5rem 1.5rem"
              onClick={() => {
                denyCallback?.();
                handleClose();
              }}
            >
              {denyText || t('No')}
            </Button>
          </Stack>
        </StyledModal>
      </MUIModal>
    </>
  );
};

export default Modal;
